import React, { useState, useEffect, useRef } from 'react';
import useUserPlan from '../hooks/useUserPlan';
import { LENSES } from '../utils/modes';
import { FiInfo, FiGrid, FiX, FiLock } from "react-icons/fi";
import { createPortal } from 'react-dom';

// --- Tiered Access Helpers ---
const PLAN_LEVELS = {
  free: 0,
  professional: 1,
  elite: 2,
};

function hasAccess(userPlan, requiredPlan) {
  const cleanUserPlan = userPlan ? userPlan.trim().toLowerCase() : 'free';
  const userLevel = PLAN_LEVELS[cleanUserPlan] || 0;
  const requiredLevel = PLAN_LEVELS[requiredPlan];

  if (requiredLevel === undefined) {
    return true;
  }
  
  return userLevel >= requiredLevel;
}

// --- HELPER FUNCTIONS & DATA ---
const logButtonClick = (label) => {
  console.log(`[Button Clicked] ${label} at ${new Date().toISOString()}`);
};

const STRATEGY_REQUIREMENTS = {
  "Root Cause Analysis": 'professional',
  "Behavioral Insights": 'professional',
  "Strategic Tradeoffs": 'professional',
  "Decision Overlays™": 'elite',
};

const groupedStrategies = {
  Reframing: [
    { label: "Reframe", prompt: `Reframe the user's idea...` },
    { label: "Reframe by Market Sector", prompt: `Reframe the concept for a different market...` },
    { label: "Inversion", prompt: `Invert the concept. Ask: "What would I do if I wanted this idea to fail spectacularly?"...` },
  ],
  "Positioning & Market Fit": [
    { label: "Predict ROI", prompt: `Estimate the potential return on investment...` },
    { label: "Predict Disruption Likelihood", prompt: `Evaluate how likely the idea is to disrupt existing players...` },
    { label: "Market Success Predictor", prompt: `Score the idea on 5 factors...` },
    { label: "Unfair Advantage", prompt: `Identify any potential unfair advantages...` },
    { label: "10x Thinking", prompt: `Challenge the idea by asking: How could this be 10x better...` },
    { label: "Simulate Scenario", prompt: `Simulate a scenario where a key market variable changes...` },
  ],
  "Root Cause Analysis": [
    { label: "First Principles Breakdown", prompt: `Deconstruct the idea using First Principles Thinking...` },
    { label: "Assumption Audit", prompt: `Identify all core assumptions the idea depends on...` },
    { label: "Pre-Mortem Analysis", prompt: `Imagine the idea has failed. Work backward...` },
  ],
  "Behavioral Insights": [
    { label: "Debias Me", prompt: `Evaluate the idea for common cognitive biases...` },
    { label: "Objectify Response", prompt: `Rephrase the concept in purely objective terms...` },
    { label: "Behavioral Triggers", prompt: `Identify psychological triggers that could drive user action...` },
    { label: "Jobs To Be Done", prompt: `Frame the idea around a core user job-to-be-done...` },
  ],
  "Strategic Tradeoffs": [
    { label: "Tradeoffs", prompt: `List the major tradeoffs involved in pursuing this idea...` },
    { label: "Tradeoff Matrix", prompt: `Create a 2x2 matrix to visualize strategic tradeoffs...` },
  ],
 "Decision Overlays™": [
    {
      label: "Executive Metrics Pack™",
      kind: "tool",
      toolKey: "exec_metrics",
      requiresAnalysis: true,
      prompt: `You are Executive Metrics Pack™. Your job is to produce a crisp, 1-page board memo.

If the input already looks like an analysis (headings, bullets, causal reasoning), DO NOT add new ideas—only format and distill.
If it’s just a raw brief, first do a minimal internal analysis (not shown) and then produce the memo.

OUTPUT EXACTLY with these headings:
1) Decision Summary (2 sentences: include the primary path + explicit risk lever)
2) KPIs & Targets (bullets with numeric ranges; each tied to cost, speed, quality, or adoption)
3) Assumptions to Test (≤5, each with the fastest validation step)
4) Timeline & Owners (Now, 30d, 90d — always assign to a clear role, not a vague actor)
5) Go/No-Go Triggers (clear thresholds tied to CAC, LTV, ROI, or retention)

Rules:
- 300–450 words total.
- Boardroom-ready, investor-grade tone: concise, specific, no fluff.
- No brainstorming language.
- If data is missing, use “TBD” + the fastest validation step.
- End with a bold Provocation (1 line challenging the user’s assumption).`
    },
    {
      label: "Strategic Tradeoff Simulator™",
      kind: "tool",
      toolKey: "tradeoff_sim",
      suggestedStyles: ["Adaptive Strategist","Reality Check","Market Shake-Up"],
      prompt: `You are Strategic Tradeoff Simulator™. Given a user brief, simulate at least three distinct strategic approaches, then compare them.

Steps:
1) Generate analyses from multiple strategic angles (Adaptive Strategist, Reality Check, Market Shake-Up).
2) Score each analysis on: ROI, Risk, Speed, Reversibility, Evidence Strength (0–10 each).
   - Each score MUST have a 1-sentence rationale.
   - Mark [A] if assumption, [E] if evidence.
   - At least one score per strategy must cite an external precedent (e.g., CAC benchmarks, B2B adoption rates).
   - 0 = catastrophic; 10 = best-in-class.
3) Create a comparative summary table with the scores.
4) Conclude with:
   - Ranked Recommendation (#1–3, no ties) with one-line rationales
   - When It Wins / When It Fails (bullets for the #1 pick)
   - Key Assumptions to Validate Next (≤5 bullets)
   - End with a bold Provocation.

Rules:
- 300–450 words total.
- Crisp, boardroom tone — decision-focused, not speculative.
- If insufficient data, mark “TBD” with the fastest validation step.`
    },
    {
      label: "Elite Scenario Chains™",
      kind: "tool",
      toolKey: "scenario_chains",
      prompt: `You are Elite Scenario Chains™. Generate branching foresight for the user's brief.

Return THREE scenarios with the headings exactly:
A) Base Case
B) Stretch Case
C) Downside Case

For EACH scenario include:
• Leading Indicators (3–5 early weak signals, include at least one numeric threshold)
• Trigger Points (2–3 binary thresholds for action, include at least one numeric threshold)
• First 3 Moves (who/what/when — must assign to a role with time horizon)
• Kill Criteria (at least one financial threshold: CAC, ROI, runway + one operational threshold: churn, supply, service)
• Downside Case must end with a learning action (pivot, adjust, or reframe next steps)

Rules:
- Stretch Case must be ambitious but plausible (≤2× Base Case metrics, no fantasy virality).
- Differentiate clearly between Leading Indicators vs Trigger Points.
- Tactical bullets only, no filler.
- Keep output 300–450 words.
- End with a bold Provocation.`
    },
{
  label: "Unit Economics Sensitivity Pack™",
  kind: "tool",
  toolKey: "unit_sens",
  prompt: `You are Unit Economics Sensitivity Pack™. Produce an investor-grade mini-model from the user's brief.

Output EXACTLY with:
1) Assumptions Table (CAC, AOV, Gross Margin %, Repeat Rate/Month, Churn %, Monthly Ops Cost; include numeric ranges when unknown)
2) Core Metrics (LTV, LTV:CAC, Gross Profit/Customer, Payback Period in months)
3) Sensitivity (how LTV:CAC and Payback move at CAC ±20% and Churn ±20%)
4) Break-even & Runway (units to break even; months of runway at current burn if stated; mark TBD if missing)
5) Actions (3 moves to improve the tightest constraint: cost, conversion, or retention)

Rules:
- 300–450 words. Use a compact table for the Assumptions.
- If data is missing, mark “TBD” + the fastest validation step.
- Use explicit formulas or short parentheticals (e.g., LTV = AOV × gross margin × repeats).
- End with a bold Provocation.`
},
{
  label: "Investor Diligence Interrogator™",
  kind: "tool",
  toolKey: "diligence_interrogator",
  prompt: `You are Investor Diligence Interrogator™. Run a partner-meeting style grill to reveal weaknesses fast.

Output EXACTLY with:
1) Thesis in One Line (plain, falsifiable)
2) Top 7 Diligence Questions (short, surgical; each tied to a risk: market, channel, unit economics, ops, legal, team, moat)
3) Evidence Bar (what would count as “green light” evidence for each question)
4) Dealbreakers (3 hard red lines with numeric thresholds)
5) Resolve Plan (3 specific experiments to flip the top 2 risks within 30 days)

Rules:
- 300–450 words, boardroom tone.
- No fluff. Each question must connect to a risk and an evidence bar.
- If data is missing, use “TBD” + fastest validation.
- End with a bold Provocation.`
},
{
  label: "Red Team Pre-Mortem & Countermeasures™",
  kind: "tool",
  toolKey: "redteam_premortem",
  prompt: `You are Red Team Pre-Mortem & Countermeasures™. Assume the initiative failed. Explain why—and how to avert it.

Output EXACTLY with:
A) Failure Narrative (≤120 words; plausible, not sensational)
B) Root Causes (max 5; each mapped to People, Process, Tech, Market, Capital)
C) Tripwires (leading indicators with numeric thresholds)
D) Countermeasures (one per root cause; who/what/when)
E) Hard Stops (clear numeric stop-loss rules)

Rules:
- 300–450 words. Tactical bullets. No filler.
- Include at least one financial and one operational threshold.
- End with a bold Provocation.`
},
{
  label: "7-Day MVT Launcher™",
  kind: "tool",
  toolKey: "mvt_launcher",
  prompt: `You are 7-Day MVT Launcher™. Design the fastest test to de-risk the brief within one week.

Output EXACTLY with:
1) Risk Focus (the single riskiest assumption)
2) Test Design (how to validate in ≤7 days; channels, sample size, offer)
3) Success/Fail Criteria (numeric thresholds)
4) Data Plan (what to track; how to attribute cause/effect)
5) Ops Checklist (assets, roles, timeline by day)
6) Next Move Logic (what to do on Pass / Borderline / Fail)

Rules:
- 300–450 words. No fluff. Every section must contain numbers or concrete steps.
- If inputs are missing, mark “TBD” + fastest way to fill.
- End with a bold Provocation.`
},
{
  label: "Capital Allocation Gate™",
  kind: "tool",
  toolKey: "cap_gate",
  prompt: `You are Capital Allocation Gate™. Recommend where to put the next dollar—and where to stop.

Output EXACTLY with:
1) Allocation Thesis (one paragraph, grounded in unit economics)
2) Portfolio Split (e.g., % to Channel A/B/C with rationale)
3) Stage Gates (numeric thresholds for continuing/pausing each workstream)
4) Monitoring Cadence (who reviews what, and how often; include one dashboard spec)
5) Kill/Double-Down Rules (explicit financial + operational triggers)

Rules:
- 300–450 words. Use a compact table for Portfolio Split if helpful.
- No vague language; every gate must be numeric.
- End with a bold Provocation.`
}
  ],

};

function getRandomStrategy() {
  const all = Object.values(groupedStrategies).flat().filter(s => s.prompt !== "[RANDOMIZED]");
  const a = all[Math.floor(Math.random() * all.length)];
  let b;
  do { b = all[Math.floor(Math.random() * all.length)]; } while (b.label === a.label);
  return `You are Surprise Me™. Take inspiration from TWO elite tools below. 
Your task is to fuse their methods into a single, coherent response. 
Do not output them separately — integrate them.

--- TOOL A ---
${a.prompt}

--- TOOL B ---
${b.prompt}

Integration Rules:
- Weave their frameworks together into one cohesive memo (no duplicate sections).
- Surface contradictions explicitly as tensions to resolve.
- Deliver in under 500 words, in an authoritative executive voice.
- Conclude with one bold provocation to challenge the user’s assumptions.`;
}

// --- MAIN COMPONENT ---
export default function StrategicToolbar({
  onStrategyClick,
  activeMode,
  setActiveMode,
  setUpgradeModalOpen,
}) {
    const containerRef = useRef(null);

  const [openPanel, setOpenPanel] = useState(null);
  const [selectedStyle, setSelectedStyle] = useState(LENSES[0]);
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const userPlan = useUserPlan();
  const [showThinkingInfo, setShowThinkingInfo] = useState(false);
  const [hoveredLens, setHoveredLens] = useState(null);
  const [tooltipContent, setTooltipContent] = useState('');
   const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0, opacity: 0, transform: '' });
 const isTouch = typeof window !== 'undefined' && (('ontouchstart' in window) || navigator.maxTouchPoints > 0);

 
  // Block vertical pan that starts on the toolbar so it can’t drag the viewport
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const onMove = (e) => {
      // allow vertical pan ONLY inside elements that declare themselves scrollable
      if (e.target.closest('[data-allow-pan-y]')) return;
      e.preventDefault(); // stop the pull-down / scroll-chain
    };

    el.addEventListener('touchmove', onMove, { passive: false });
    return () => el.removeEventListener('touchmove', onMove);
  }, []);

const STRATEGY_DESCRIPTIONS = {
  // Reframing
  "Reframe": "Look at the user's idea from a completely different perspective to uncover new possibilities.",
  "Reframe by Market Sector": "Adapt the core concept to fit an entirely new industry or customer segment.",
  "Inversion": "Identify potential points of failure by asking what you would do to guarantee the idea fails.",

  // Positioning & Market Fit
  "Predict ROI": "Generate a high-level estimate of the potential return on investment for the idea.",
  "Predict Disruption Likelihood": "Assess the idea's potential to disrupt the current market and its key players.",
  "Market Success Predictor": "Score the idea against five key factors to forecast its likelihood of market success.",
  "Unfair Advantage": "Pinpoint any unique, hard-to-copy advantages the idea possesses.",
  "10x Thinking": "Brainstorm ways to make the idea ten times more ambitious, effective, or impactful.",
  "Simulate Scenario": "Model how the idea would perform if a critical market variable (e.g., cost, competition) changes.",

  // Root Cause Analysis
  "First Principles Breakdown": "Deconstruct the idea into its most fundamental truths to build it back up from a solid foundation.",
  "Assumption Audit": "List and challenge all the core assumptions the idea relies on to be successful.",
  "Pre-Mortem Analysis": "Imagine the idea has already failed one year from now, and work backward to identify the most likely causes.",

  // Behavioral Insights
  "Debias Me": "Check the idea for common cognitive biases (e.g., confirmation bias) that might be clouding judgment.",
  "Objectify Response": "Describe the concept using only neutral, objective language, stripping away emotional or persuasive framing.",
  "Behavioral Triggers": "Identify key psychological triggers that can be used to encourage user adoption and engagement.",
  "Jobs To Be Done": "Reframe the idea not as a product, but as a solution to a specific 'job' a user is trying to get done.",

  // Strategic Tradeoffs
  "Tradeoffs": "Outline the key compromises and opportunity costs associated with pursuing this idea.",
  "Tradeoff Matrix": "Create a 2x2 grid to visualize the strategic choices and their consequences (e.g., Speed vs. Quality).",

  // Elite / Overlays (existing)
  "Executive Metrics Pack™": "Generates a concise, 1-page board memo with KPIs, owners, timelines, and go/no-go triggers.",
  "Strategic Tradeoff Simulator™": "Recasts the brief into a side-by-side options matrix with ROI/Risk/Speed scores and a ranked pick.",
  "Elite Scenario Chains™": "Builds base/stretch/downside playbooks with leading indicators, triggers, and kill criteria.",

  // Elite / Overlays (new)
  "Unit Economics Sensitivity Pack™": "Creates a mini financial model (LTV, LTV:CAC, payback) with ±20% CAC/churn sensitivities and break-even/runway.",
  "Investor Diligence Interrogator™": "Runs a partner-meeting style grill: top risks, evidence bars, hard dealbreakers, and a 30-day resolve plan.",
  "Red Team Pre-Mortem & Countermeasures™": "Assumes failure to surface root causes, tripwires, countermeasures, and strict stop-loss rules.",
  "7-Day MVT Launcher™": "Designs a one-week experiment with risk focus, test design, numeric success/fail thresholds, data plan, and next-move logic.",
  "Capital Allocation Gate™": "Directs the next dollar via portfolio split, numeric stage gates, monitoring cadence, and kill/double-down triggers.",

  // Bundle label (if you show a single condensed line in the Elite plan card)
  "Decision Overlays™": "Unified elite overlays that convert any response into board memos, tradeoff matrices, and scenario playbooks—decision-ready in one click.",

  // Fun slot
  "Surprise Me": "Applies a powerful strategic model at random for an unexpected—but structured—insight."
};

  useEffect(() => {
    const found = LENSES.find((m) => m.label === activeMode) || LENSES[0];
    setSelectedStyle(found);
  }, [activeMode]);

  const togglePanel = (panelName) => {
    setOpenPanel(openPanel === panelName ? null : panelName);
  };
  
const handleStrategyClick = (strategy, groupName) => {
  const requiredPlan = STRATEGY_REQUIREMENTS[groupName];
  if (requiredPlan && !hasAccess(userPlan, requiredPlan)) {
    const tier = requiredPlan === 'elite' ? 'elite' : 'proOrElite';
    setUpgradeModalOpen(tier);
    return;
  }

  logButtonClick(strategy.label);

  const template = strategy.prompt === "[RANDOMIZED]" ? getRandomStrategy() : strategy.prompt;
  const short = `🧠 Strategy Applied: ${strategy.label}`;

  const payload = {
    // show this as the user's turn so the parent/backend doesn't reject it
    prompt: short,
    input: short,                // keeps compatibility if the parent uses `input`
    visibleUserMessage: short,

    // actual instructions for the model (hidden from the conversation)
    systemPrompt: template,

    activeMode,
    meta: { source: 'strategy', key: strategy.label } // optional
  };

  onStrategyClick(payload);

  try { localStorage.removeItem('lastDraft'); } catch {}
  setOpenPanel(null);
  if (isMobileMenuOpen) setMobileMenuOpen(false);
  handleStrategyMouseLeave();
};

 const handleStrategyMouseEnter = (e, strategy) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const isMobile = window.innerWidth < 768; // Tailwind's 'md' breakpoint

    let positionStyles = {};
 const margin = 12;                 // keep a little gutter
const estWidth = 224;              // ~ w-56 (fallback estimate for clamping)

    if (isMobile) {
    // center above the icon, then clamp inside the viewport
    let left = rect.left + rect.width / 2;
    const half = estWidth / 2;
    left = Math.max(margin + half, Math.min(left, window.innerWidth - margin - half));
    positionStyles = {
      left,
      top: rect.top - 8,
      transform: 'translate(-50%, -100%)',
   };
   } else {
  // On desktop, position the tooltip consistently to the left
  const tooltipWidth = estWidth; // ~224px (or measure dynamically if you want)
  positionStyles = {
    left: rect.left,
    top: rect.top + rect.height / 2,
    transform: `translate(calc(-100% - ${margin}px), -50%)`, 
    // pushes tooltip fully left of the button + margin
    minWidth: `${tooltipWidth}px`,
  };
}

    setTooltipContent(STRATEGY_DESCRIPTIONS[strategy.label] || 'No description available.');
    setTooltipPosition({
      ...positionStyles,
      opacity: 1,
    });
  };



const handleIconClick = (e, strategy) => {
    e.stopPropagation(); // Prevents the parent button's onClick from firing
    if (tooltipPosition.opacity > 0 && tooltipContent === STRATEGY_DESCRIPTIONS[strategy.label]) {
      handleStrategyMouseLeave(); // If this tooltip is already open, close it
    } else {
      handleStrategyMouseEnter(e, strategy); // Otherwise, show it
    }
  };
  const handleStrategyMouseLeave = () => {
    setTooltipPosition(prev => ({ ...prev, opacity: 0 }));
  };

useEffect(() => {
  if (tooltipPosition.opacity === 0) return;
 const onDocPointerDown = (ev) => {
   // if the press originates on the info icon, don't close
   if (ev.target?.closest?.('.info-icon')) return;
   setTooltipPosition(prev => ({ ...prev, opacity: 0 }));
 };
 document.addEventListener('pointerdown', onDocPointerDown);
 return () => document.removeEventListener('pointerdown', onDocPointerDown);
}, [tooltipPosition.opacity]);


return (
    <>
      {/* TOOLTIP PORTAL: Renders tooltip outside of the component's DOM tree to avoid layout conflicts */}
      {tooltipPosition.opacity > 0 && createPortal(
       <div
  className="fixed p-2 md:w-56 max-w-[calc(100vw-24px)] bg-gray-950 border border-purple-500/50 rounded shadow-2xl text-xs text-white/90 z-[200] pointer-events-none transition-opacity duration-200 whitespace-normal break-words"
  style={{ top: tooltipPosition.top, left: tooltipPosition.left, transform: tooltipPosition.transform, opacity: tooltipPosition.opacity }}
>
          {tooltipContent}
        </div>,
        document.body
      )}

      {/* MOBILE POPUP MODAL */}
{isMobileMenuOpen && (
  <div
    className="fixed inset-0 z-[100] bg-black/80 flex justify-center items-center animate-fadeIn overscroll-none touch-none"
    onClick={() => setMobileMenuOpen(false)}
  >
    <div
      className="bg-gray-900 border border-purple-500/30 text-white rounded-lg shadow-2xl p-4 max-w-sm w-11/12 flex flex-col max-h-[85vh]"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex-none flex justify-between items-center border-b border-white/10 pb-3 mb-4">
        <h2 className="text-xl font-semibold">Strategic Tools</h2>
        <button onClick={() => setMobileMenuOpen(false)} className="p-1">
          <FiX size={24} />
        </button>
      </div>

            {/* Make this the only scrollable area inside the modal */}
      <div data-allow-pan-y className="flex-1 space-y-4 overflow-y-auto pr-2">
              <div className="relative">
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2">
                    <span>Lens:</span>
                    <FiInfo className="text-white/60" onClick={(e) => { e.stopPropagation(); setShowThinkingInfo(!showThinkingInfo); }} />
                  </label>
                  <select
                    value={activeMode}
                    onChange={(e) => {
                      const selected = LENSES.find(m => m.label === e.target.value);
                      if (selected && !hasAccess(userPlan, selected.requiredPlan)) {
                        setUpgradeModalOpen('elite');
                        setMobileMenuOpen(false); 
                      } else {
                        setActiveMode(e.target.value);
                      }
                    }}
                    className="bg-gray-800 text-white p-1 rounded w-full"
                  >
                    {LENSES.map(({ label, emoji, requiredPlan }) => {
                      const isAccessible = hasAccess(userPlan, requiredPlan);
                      const planName = requiredPlan.charAt(0).toUpperCase() + requiredPlan.slice(1);
                      return (
                        <option key={label} value={label} className="bg-gray-900 text-white">
                          {emoji} {label}
                          {!isAccessible ? ` (${planName} Only)` : ''}
                        </option>
                      );
                    })}
                  </select>
                </div>
                {showThinkingInfo && ( <div className="mt-2 text-xs text-white/80 p-2 bg-black/50 rounded">Select a Mode to influence the tone and angle of the AI's thinking.</div> )}
                {selectedStyle.description && !showThinkingInfo && (
                    <div className="mt-2 text-xs text-white/80 p-2 bg-black/50 rounded animate-fadeIn">
                        <strong>{selectedStyle.label}:</strong> {selectedStyle.description}
                    </div>
                )}
              </div>

              {Object.entries(groupedStrategies).map(([group, strategies]) => {
                // ✅ FIX: Changed STRATEGIES to STRATEGY_REQUIREMENTS
                const requiredPlan = STRATEGY_REQUIREMENTS[group];
                const canAccess = hasAccess(userPlan, requiredPlan);
                return (
                  <div key={group}>
                    <h3 className="font-semibold text-purple-400 mb-2 border-t border-white/10 pt-3 flex items-center gap-2">
                      {group}
                      {!canAccess && <FiLock className="text-amber-400" size={12} />}
                    </h3>
                    <div className="flex flex-col space-y-1">
                      {strategies.map((s) => (
                        <div 
                          key={s.label} 
                          className="relative group"
                        >
                          <button
                            onClick={() => handleStrategyClick(s, group)}
                               onMouseEnter={!isTouch ? handleStrategyMouseLeave : undefined}   // hovering the button itself hides tooltip
                            className="w-full text-left px-3 py-2 bg-gray-900 hover:bg-gray-800 rounded text-sm flex items-center justify-between gap-2"
                          >
                            <span>{s.emoji} {s.label}</span>

                         {/* icon gets its own hitbox + event isolation */}
     <span
       className="info-icon inline-flex items-center justify-center w-4 h-4 flex-shrink-0"
       onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); }} // block parent button
       onClick={(e) => handleIconClick(e, s)}                             // toggle tooltip
       onMouseEnter={!isTouch ? (e) => handleStrategyMouseEnter(e, s) : undefined}
       onMouseLeave={!isTouch ? handleStrategyMouseLeave : undefined}
     >
       <FiInfo className="text-white/50 hover:text-white transition-colors" />
     </span>


                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* MAIN TOOLBAR CONTAINER */}
<div
  ref={containerRef}
  className="sticky bottom-0 z-10 bg-black text-white rounded-t-xl shadow-2xl touch-none pb-[env(safe-area-inset-bottom)]"
>    
<div className="h-0.5 w-full bg-purple-600 blur-[1px] opacity-70"></div>
        <div className="pt-3 px-4 pb-1">
          <div className="md:hidden">
            <div className="relative group">
              <div className="absolute -inset-0.5 bg-purple-600 rounded-lg blur opacity-75 group-hover:opacity-100 transition duration-200"></div>
              <button
                onClick={() => setMobileMenuOpen(true)}
                className="relative w-full flex items-center justify-center gap-2 bg-black text-white py-2 rounded-lg transition"
              >
                <FiGrid /> Strategic Tools
              </button>
            </div>
          </div>

          <div className="hidden md:flex flex-row justify-between items-center">
            <div className="flex items-center gap-2 relative">
              <span>Current Mode:</span>
              <div className="relative group">
                <div className="absolute inset-0 rounded-lg shadow-[0_0_8px_rgba(168,85,247,0.3)] group-hover:shadow-[0_0_12px_rgba(168,85,247,0.4)] transition"></div>
                <button onClick={() => togglePanel("thinkingStyleDesktop")} className="relative text-sm bg-black px-1.5 py-1 rounded transition">
                  {selectedStyle?.emoji} {activeMode}
                </button>
                
                {openPanel === "thinkingStyleDesktop" && (
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 bg-black border border-purple-500/30 p-2 rounded shadow-xl flex flex-col gap-1 z-50">
                    {LENSES.filter(({ label }) => label !== activeMode).map(({ label, emoji, requiredPlan, description }) => {
                      if (hasAccess(userPlan, requiredPlan)) {
                        return (
                          <div key={label} className="relative flex items-center">
                            <button onClick={() => { setActiveMode(label); setOpenPanel(null); }}
                              className="w-full px-3 py-1.5 bg-gray-900 hover:bg-gray-800 rounded text-sm flex items-center justify-between gap-4 whitespace-nowrap">
                              <span>{emoji} {label}</span>
                              <FiInfo 
                                className="text-white/50 hover:text-white transition-colors"
                                onMouseEnter={() => setHoveredLens(label)}
                                onMouseLeave={() => setHoveredLens(null)}
                              />
                            </button>
                            {hoveredLens === label && (
                              <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 p-2 w-56 bg-gray-950 border border-purple-500/50 rounded shadow-2xl text-xs text-white/90 z-[60] pointer-events-none animate-fadeIn">
                                {description}
                              </div>
                            )}
                          </div>
                        );
                      } else {
                        const planName = requiredPlan.toUpperCase();
                        return (
                          <div key={label} className="relative flex items-center">
                            <button onClick={() => { setUpgradeModalOpen('elite'); setOpenPanel(null); }}
                              className="w-full px-3 py-1.5 bg-gray-800/50 rounded text-sm flex items-center justify-between gap-3 whitespace-nowrap cursor-pointer hover:bg-gray-800/70 transition-colors">
                              <span className="text-gray-500">{emoji} {label}</span>
                              <span className="font-bold text-amber-400 text-xs bg-amber-400/10 px-2 py-0.5 rounded-full">{planName} ONLY</span>
                            </button>
                          </div>
                        );
                      }
                    })}
                  </div>
                )}
              </div>
              <FiInfo className="text-white/60 cursor-pointer" onClick={() => { setShowThinkingInfo(!showThinkingInfo); }} />
              {showThinkingInfo && ( <div className="absolute bottom-full mb-2 left-0 bg-black/80 text-xs text-white/80 px-3 py-2 rounded shadow-xl z-50 w-64">Select a Mode to influence the tone and angle of the AI's thinking.</div> )}
            </div>
            
            <div className="flex flex-wrap gap-2">
              {Object.entries(groupedStrategies).map(([group, strategies]) => {
                const requiredPlan = STRATEGY_REQUIREMENTS[group];
                const canAccess = hasAccess(userPlan, requiredPlan);
                return (
                  <div key={group} className="relative">
                    <div className="relative group">
                      <div className="absolute inset-0 rounded-lg shadow-[0_0_8px_rgba(168,85,247,0.3)] group-hover:shadow-[0_0_12px_rgba(168,85,247,0.4)] transition"></div>
                      <button 
                        onClick={() => {
                          if (!canAccess) {
                            setUpgradeModalOpen('elite');
                          } else {
                            togglePanel(group);
                          }
                        }} 
                        className={`relative text-sm px-2 py-1.5 rounded transition-colors ${
                          canAccess ? 'bg-black hover:bg-gray-800' : 'bg-gray-900/80 cursor-not-allowed'
                        }`}
                      >
                        <span className={!canAccess ? 'opacity-30' : ''}>{group}</span>
                        {!canAccess && (
                          <div className="absolute inset-0 flex items-center justify-center bg-black/30 rounded">
                            <FiLock className="text-white/70" size={16} />
                          </div>
                        )}
                      </button>
                    </div>
                    {openPanel === group && canAccess && (
  <div
    data-allow-pan-y
    className="absolute bottom-full right-0 mb-2 bg-black border border-purple-500/30 p-2
               rounded shadow-2xl z-50 max-h-[60vh] overflow-auto min-w-[240px] w-auto flex flex-col gap-1"
  >                        {strategies.map((s) => (
                          <div key={s.label} className="relative group">
                            <button
                              onClick={() => handleStrategyClick(s, group)}
                              className="w-full text-left px-3 py-2 bg-gray-900 hover:bg-gray-800 rounded text-sm flex items-center justify-between gap-2 whitespace-nowrap"
                            >
                              <span>{s.emoji} {s.label}</span>
                              <FiInfo 
                                className="text-white/50 hover:text-white transition-colors flex-shrink-0"
                                onMouseEnter={(e) => handleStrategyMouseEnter(e, s)}
                                onMouseLeave={handleStrategyMouseLeave}
                                                            onClick={(e) => handleIconClick(e, s)}

                              />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}